import { FC, memo } from 'react'
import ReactMarkdown, { defaultUrlTransform, Options } from 'react-markdown'
import { ComponentProps } from 'react'
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter'
import { oneLight } from 'react-syntax-highlighter/dist/esm/styles/prism'
import posthog from 'posthog-js'
import { useTheme } from '@/app/chatbot/lib/themeContext'
import rehypeExternalLinks from 'rehype-external-links'
import remarkGfm from 'remark-gfm'

interface CustomLinkProps extends ComponentProps<'a'> {
  href?: string
  children?: React.ReactNode
}

/**
 * CustomLink component that handles different types of links
 * - Special product links are rendered as styled buttons
 * - Checkout links are rendered as prominent green buttons
 * - Regular links are rendered with tracking and proper attributes
 */
const CustomLink = ({ href, children }: CustomLinkProps) => {
  // Check if the link matches our special pattern
  const isKCbobcatProductLink = href?.includes('kcbobcat.com/for-sale/')

  if (isKCbobcatProductLink) {
    return (
      <button
        className="rounded bg-[#11A146] px-4 py-2 font-semibold text-white transition-colors"
        onClick={() => {
          posthog.capture('checkout_link_clicked', {
            href: href,
            linkText: typeof children === 'string' ? children : 'non-text link'
          })
          window.open(href, '_blank')
        }}
      >
        {children}
      </button>
    )
  }

  const isCheckoutLink = href?.includes('cloudstore.co/checkout')

  if (isCheckoutLink) {
    return (
      <button
        className="rounded-full bg-green-500 px-4 py-2 font-semibold text-white transition-colors hover:bg-green-600"
        onClick={() => {
          posthog.capture('checkout_link_clicked', {
            href: href,
            linkText: typeof children === 'string' ? children : 'non-text link'
          })
          window.open(href, '_blank')
        }}
      >
        {children}
      </button>
    )
  }

  const isArrowPayLink = href?.includes('arrowpay.ai')

  if (isArrowPayLink) {
    return (
      <button
        className="rounded-full bg-green-500 px-4 py-2 font-semibold text-white transition-colors hover:bg-green-600"
        onClick={() => {
          window.open(href, '_blank')
        }}
      >
        {children}
      </button>
    )
  }

  // Default link rendering with tracking
  return (
    <a
      className="not-prose underline"
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      onClick={() => {
        posthog.capture('link_clicked', {
          href: href,
          linkText: typeof children === 'string' ? children : 'non-text link'
        })
      }}
    >
      {children}
    </a>
  )
}

CustomLink.displayName = 'CustomLink'

/**
 * MarkdownWrapper component that enhances ReactMarkdown with:
 * 1. Auto-link detection (converts plain URLs to clickable links)
 * 2. Custom styling for different elements
 * 3. Syntax highlighting for code blocks
 * 4. Special handling for different types of links
 */
const MarkdownWrapper = (props: Options) => (
  <ReactMarkdown
    urlTransform={url => {
      return url.startsWith('tel:') ? url : defaultUrlTransform(url)
    }}
    // remarkGfm enables GitHub Flavored Markdown features including autolink literals
    // This allows plain URLs to be automatically converted to clickable links
    remarkPlugins={[remarkGfm]}
    // rehypeExternalLinks adds proper attributes to external links for security and UX
    rehypePlugins={[
      [
        rehypeExternalLinks,
        { target: '_blank', rel: ['nofollow', 'noopener', 'noreferrer'] }
      ]
    ]}
    {...props}
    components={{
      ...props.components,
      p: ({ children }) => (
        <p style={{ wordBreak: 'break-word' }} className="break-words">
          {children}
        </p>
      ),
      ol: ({ children }) => (
        <ol className="list-inside list-decimal p-0">{children}</ol>
      ),
      ul: ({ children }) => (
        <ul className="list-inside list-disc p-0">{children}</ul>
      ),
      // Use our CustomLink component for all anchor tags
      a: CustomLink,
      code(props) {
        const { children, className, ...rest } = props
        const match = /language-(\w+)/.exec(className || '')
        return match ? (
          <SyntaxHighlighter
            PreTag="div"
            className="!rounded-xl border text-sm"
            language={match[1]}
            style={oneLight}
          >
            {String(children).replace(/\n$/, '')}
          </SyntaxHighlighter>
        ) : (
          <code {...rest} className={className}>
            {children}
          </code>
        )
      }
    }}
  />
)

MarkdownWrapper.displayName = 'MarkdownWrapper'

/**
 * Memoized version of the markdown component for performance optimization
 * Only re-renders when content (children) or className changes
 */
export const MemoizedReactMarkdown: FC<Options> = memo(
  MarkdownWrapper,
  (prevProps, nextProps) =>
    prevProps.children === nextProps.children &&
    prevProps.className === nextProps.className
)
